import type { ReactNode } from 'react';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/assist/ui/sidebar';
import { InsightsNavProvider } from '@/contexts/InsightsNavContext';
import { AppSidebar } from './AppSidebar';
import { ThemeToggle } from '@/components/assist/dashboard/header/ThemeToggle';
import { HelpButton } from '@/components/assist/dashboard/header/HelpButton';
import { GlossaryButton } from '@/components/assist/dashboard/header/GlossaryButton';
import { UserProfileDropdown } from '@/components/assist/dashboard/header/UserProfileDropdown';
import { NotificationsDropdown } from '@/components/assist/dashboard/header/NotificationsDropdown';
import { PhaseOutUserWarning } from '@/components/PhaseOutUserWarning';
import { Toaster } from '@/components/assist/ui/toaster';
import { SupportDrawerProvider } from '@/contexts/SupportDrawerContext';
import { SupportDrawer } from '@/components/support/SupportDrawer';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useTenant } from '@/contexts/TenantContext';

interface DashboardLayoutProps {
  children: ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { currentTenant } = useTenant();

  const content = (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <SidebarInset className="flex-1 min-w-0">
          <header className="sticky top-0 z-10 flex h-14 items-center gap-2 px-4 md:px-6 border-b border-border/50 bg-background/80 backdrop-blur-sm">
            <SidebarTrigger className="h-9 w-9" />
            <div className="flex-1" />
            <LanguageSwitcher
              isAuthenticated
              variant="compact"
              allowedLanguages={currentTenant?.allowedLanguages}
            />
            <NotificationsDropdown />
            <ThemeToggle />
            <GlossaryButton />
            <HelpButton />
            <UserProfileDropdown />
          </header>
          <main className="flex-1 p-4 md:p-6 lg:p-8">
            <PhaseOutUserWarning />
            {children}
          </main>
        </SidebarInset>
      </div>
      <Toaster />
    </SidebarProvider>
  );

  const withSupport = (
    <SupportDrawerProvider>
      {content}
      <SupportDrawer />
    </SupportDrawerProvider>
  );

  return (
    <InsightsNavProvider>
      {withSupport}
    </InsightsNavProvider>
  );
}
